import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '../lib/router';
import { cx } from './ui';

export interface HelpText {
  title: string;
  text: string;
  example?: string;
  /** Academy lesson that explains this in full (a path, e.g. /academy/leverage-margin/margin-used). */
  learn?: string;
}

const WIDTH = 280;
const GAP = 8;
const EDGE = 12;
const canHover = () => typeof matchMedia !== 'undefined' && matchMedia('(hover: hover)').matches;

/**
 * A small "?" next to a label that explains it. Hover shows the card on desktop; a click or tap
 * pins it open until the next tap, Escape, scroll or click elsewhere. `icon="info"` draws a plain
 * "ⓘ" instead, matching the USDT chip.
 */
export function HelpTip({ help, icon = 'help', className }: { help: HelpText; icon?: 'help' | 'info'; className?: string }) {
  const [open, setOpen] = useState<'hover' | 'click' | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(null), []);

  useLayoutEffect(() => {
    if (!open || !btn.current || !pop.current) return setPos(null);
    const r = btn.current.getBoundingClientRect();
    const w = Math.min(WIDTH, innerWidth - EDGE * 2);
    const h = pop.current.offsetHeight;
    const left = Math.max(EDGE, Math.min(innerWidth - w - EDGE, r.left + r.width / 2 - w / 2));
    const below = r.bottom + GAP;
    const top = below + h > innerHeight - EDGE && r.top - GAP - h > EDGE ? r.top - GAP - h : below;
    setPos({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    addEventListener('scroll', close, true);
    addEventListener('resize', close);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
      removeEventListener('scroll', close, true);
      removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={`What is ${help.title}?`}
        aria-expanded={!!open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((o) => (o === 'click' ? null : 'click'))}
        onMouseEnter={() => canHover() && setOpen((o) => o ?? 'hover')}
        onMouseLeave={() => setOpen((o) => (o === 'hover' ? null : o))}
        className={cx(
          icon === 'info'
            ? 'inline-grid shrink-0 cursor-pointer place-items-center border-0 bg-transparent p-0 text-[0.85em] leading-none font-medium text-muted hover:text-fg aria-expanded:text-fg'
            : 'inline-grid size-[15px] shrink-0 cursor-pointer place-items-center rounded-full border border-line-strong p-0 align-[1px] text-[10px] leading-none font-semibold text-muted hover:border-muted hover:text-fg aria-expanded:border-muted aria-expanded:text-fg',
          className,
        )}
      >
        {icon === 'info' ? 'ⓘ' : '?'}
      </button>
      {open &&
        createPortal(
          <div
            ref={pop}
            id={id}
            role="tooltip"
            className="shadow-pop fixed z-50 rounded-xl border border-line-strong bg-raised px-3.5 py-3 text-left text-[12.5px] leading-normal font-normal tracking-normal text-fg normal-case"
            style={{ width: `min(${WIDTH}px, calc(100vw - ${EDGE * 2}px))`, left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
          >
            <b className="mb-1 block text-[13px] font-semibold">{help.title}</b>
            {help.text}
            {help.example && <div className="mt-2 border-t border-line pt-2 text-xs text-muted">{help.example}</div>}
            {help.learn && (
              <Link to={help.learn} onClick={close} className="mt-2 block border-t border-line pt-2 text-xs font-semibold text-accent-fg no-underline">
                Learn more in the Academy →
              </Link>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
